import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
} from "@mui/material"
import {
  BugReport as BugIcon,
  Task as TaskIcon,
  Book as StoryIcon,
  Flag as EpicIcon,
  Assignment as TaskAssignmentIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Flag,
} from "@mui/icons-material"
import { format, parseISO } from "date-fns"

import { ISSUE_TYPES, STATUS_NAMES, STATUS_COLORS, PRIORITY_NAMES, PRIORITY_COLORS } from "./roadmap"

const IssueTooltip = ({ issue, position }) => {
  const IssueIcon = ISSUE_TYPES[issue.issueType]?.icon || TaskIcon
  
  return (
    <Card sx={{ minWidth: 320, maxWidth: 450 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IssueIcon
            sx={{
              color: ISSUE_TYPES[issue.issueType]?.color,
              fontSize: 20,
              mr: 1,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {issue.key}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {issue.summary}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Issue Details */}
        <Grid container spacing={1}>
          {/* Type and Status */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Chip
                label={ISSUE_TYPES[issue.issueType]?.name}
                size="small"
                sx={{
                  bgcolor: ISSUE_TYPES[issue.issueType]?.bgColor,
                  color: ISSUE_TYPES[issue.issueType]?.color,
                  fontSize: "10px",
                  height: 20,
                }}
              />
              <Chip
                label={STATUS_NAMES[issue.status]}
                size="small"
                sx={{
                  bgcolor: STATUS_COLORS[issue.status] + "20",
                  color: STATUS_COLORS[issue.status],
                  fontSize: "10px",
                  height: 20,
                }}
              />
            </Box>
          </Grid>

          {/* Timeline with red color for overdue */}
          <Grid item xs={12}>
            {position?.isOverdue && (
              <Box sx={{ ml: 3, mt: 1, p: 1, mb: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
                <Typography variant="body2" color="error" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  ⚠️ OVERDUE ISSUE
                </Typography>
                <Typography variant="body2" color="error">
                  This issue is overdue by {position?.overdueBy}
                </Typography>
                <Typography variant="caption" color="error">
                  Expected completion: {position?.endDate}
                </Typography>
                <Typography variant="caption" color="error" display="block">
                  Current status: {STATUS_NAMES[issue.status]}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <CalendarIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
              <Typography variant="body2">
                <strong>Timeline:</strong>
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                ml: 3,
                color: position?.isOverdue ? "#d32f2f" : "text.primary",
              }}
            >
              Start: <span style={{ color: position?.isOverdue ? "#d32f2f" : "inherit" }}>{position?.startDate}</span>
            </Typography>
            <Typography
              variant="body2"
              sx={{
                ml: 3,
                color: position?.isOverdue ? "#d32f2f" : "text.primary",
              }}
            >
              End: <span style={{ color: position?.isOverdue ? "#d32f2f" : "inherit" }}>{position?.endDate}</span>
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ ml: 3 }}>
              Duration: {position?.duration}
            </Typography>
          </Grid>

          {/* Assignee */}
          {issue.assignee && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PersonIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                <Typography variant="body2">
                  <strong>Assignee:</strong>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", ml: 3 }}>
                <Typography variant="body2">{issue.assigneeName || issue.assignee || "Unassigned"}</Typography>
              </Box>
            </Grid>
          )}

          {/* Priority */}
          {issue.priority && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Flag sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                <Typography variant="body2">
                  <strong>Priority:</strong>
                </Typography>
                <Chip
                  label={PRIORITY_NAMES[issue.priority] || "Medium"}
                  size="small"
                  sx={{
                    ml: 1,
                    bgcolor: PRIORITY_COLORS[issue.priority] + "20",
                    color: PRIORITY_COLORS[issue.priority],
                    fontSize: "10px",
                    height: 18,
                  }}
                />
              </Box>
            </Grid>
          )}
          {issue.labels && issue.labels.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Labels:</strong>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {issue.labels.map((label, index) => (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "10px", height: 18 }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {issue.description && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Description:</strong>
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  fontSize: "12px",
                  maxHeight: 60,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {issue.description}
              </Typography>
            </Grid>
          )}

          {/* Epic/Parent Info */}
          {issue.epic_key && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Epic:</strong> {issue.epic_key}
              </Typography>
            </Grid>
          )}

          {issue.story_key && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Story:</strong> {issue.story_key}
              </Typography>
            </Grid>
          )}

          {/* Created/Updated dates */}
          {(issue.created || issue.updated) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              {issue.created && (
                <Typography variant="caption" color="textSecondary" display="block">
                  Created: {format(parseISO(issue.created), "MMM dd, yyyy")}
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  )
}

export default IssueTooltip